import { z } from 'zod';
/**
 * Die je Ausfahrt einstellbaren Schwellwerte.
 *
 * Dies ist die einzige Stelle, an der die Vorgabewerte stehen. Beim Anlegen
 * einer Ausfahrt werden sie in die Tabelle tour_settings geschrieben und sind
 * dort änderbar; die Datenbank hat für diese Spalten bewusst kein DEFAULT,
 * damit es die Werte nicht zweimal gibt.
 *
 * Die Feldnamen entsprechen eins zu eins den Spalten von tour_settings, nur in
 * der Schreibweise von TypeScript. Die Umsetzung geschieht in der
 * Datenzugriffsschicht des Backends.
 *
 * Diese Werte werden nach den ersten Praxisfahrten mehrfach angepasst.
 */
export const tourSettingsSchema = z.object({
    /** Rückstand in Sekunden, ab dem ein Fahrzeug als zurückgefallen gilt. */
    gapThresholdS: z.number().int().positive(),
    /**
     * So lange muss der Rückstand über der Schwelle bleiben, bevor gewarnt wird.
     * Das ist der Wert, der Ampeln von echtem Anschlussverlust trennt.
     */
    gapHoldS: z.number().int().nonnegative(),
    /** Höchstabstand zur Führungsspur, darüber gilt ein Fahrzeug als abseits der Strecke. */
    offRouteDistanceM: z.number().int().positive(),
    /**
     * Umkreis um einen Treffpunkt, ab dem jemand als **angekommen** gilt.
     *
     * Je Ausfahrt einstellbar wie `offRouteDistanceM`, weil es dasselbe ist: ein
     * Erkennungsschwellwert, der sich erst in der Praxis einpendelt. Ein einzelner
     * Treffpunkt kann ihn über `waypoint.radius_m` überschreiben — ein Parkplatz
     * ist größer als eine Kreuzung.
     *
     * Der Wert wägt zwei Fehler gegeneinander ab: Zu klein, und wer am anderen
     * Ende des Parkplatzes steht, gilt als fehlend, während er danebensteht. Zu
     * groß, und wer auf der Bundesstraße vorbeifährt, gilt als angekommen. Hundert
     * Meter fassen einen großen Parkplatz samt GPS-Streuung und reichen nicht bis
     * zur nächsten Parallelstraße.
     */
    arrivalRadiusM: z.number().int().positive(),
    /** Geschwindigkeit nahe null über diese Dauer heißt „steht". */
    standstillS: z.number().int().positive(),
    /** Ohne Positionsmeldung gilt ein Teilnehmer danach als „kein Signal". */
    noSignalTimeoutS: z.number().int().positive(),
    /** Positionen mit schlechterer Genauigkeit werden verworfen. */
    minPositionAccuracyM: z.number().int().positive(),
    /**
     * Obergrenze zur Erkennung von GPS-Ausreißern. Ein Sprung, der eine höhere
     * Geschwindigkeit als diese voraussetzen würde, ist keine Fahrt, sondern ein
     * Messfehler.
     */
    maxPlausibleSpeedMps: z.number().positive(),
    /** Mindestabstand zum letzten Spurpunkt, bevor ein neuer aufgenommen wird. */
    trackMinMoveM: z.number().positive(),
    /**
     * Lücke ohne Führungsposition, nach der ein neuer Spurabschnitt beginnt.
     * Sorgt dafür, dass die Spur des Vortages den Rückstand nicht verfälscht.
     */
    trackBreakS: z.number().int().positive(),
});
/** Vorgabewerte einer neuen Ausfahrt. */
export const DEFAULT_TOUR_SETTINGS = Object.freeze({
    gapThresholdS: 90,
    gapHoldS: 30,
    offRouteDistanceM: 150,
    arrivalRadiusM: 100,
    standstillS: 60,
    noSignalTimeoutS: 60,
    minPositionAccuracyM: 100,
    maxPlausibleSpeedMps: 60,
    trackMinMoveM: 10,
    trackBreakS: 1800,
});
//# sourceMappingURL=thresholds.js.map